import * as React from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm, type FieldPath } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Check,
  CircleCheck,
  Loader2,
  User,
} from 'lucide-react';
import { toast } from 'sonner';

import { api, ErroApi } from '@/lib/api';
import { useAuth } from '@/features/auth/contexto';
import {
  calcularIdade,
  mascararCns,
  mascararCpf,
  mascararTelefone,
  paraAsciiMaiusculo,
} from '@/lib/mascaras';
import { formatarData } from '@/lib/utils';
import {
  CAMPOS_POR_ETAPA,
  esquemaDaOrigem,
  hojeIso,
  prepararEnvio,
  type FormularioSolicitacao,
} from '@/features/solicitacoes/esquema';
import {
  caminhoDaLista,
  ehDemandaReprimida,
  escolheUbsDeOrigem,
  type OrigemSolicitacao,
} from '@/features/solicitacoes/origem';
import {
  DESCRICOES_PRIORIDADE,
  PRIORIDADES,
  ROTULOS_PRIORIDADE,
  ROTULOS_TIPO,
  TIPOS_SOLICITACAO,
} from '@/features/solicitacoes/status';
import type { Especialidade, UbsResumida } from '@/features/admin/tipos';
import type { PacientePec } from '@/features/pec/tipos';
import { BuscaPec } from '@/components/busca-pec';
import { CabecalhoPagina } from '@/components/cabecalho-pagina';
import { Botao } from '@/components/ui/button';
import { AreaTexto, Entrada, Selecao } from '@/components/ui/input';
import { Campo } from '@/components/ui/campo';
import { Cartao, CartaoConteudo } from '@/components/ui/card';
import {
  Dialogo,
  DialogoCabecalho,
  DialogoConteudo,
  DialogoDescricao,
  DialogoRodape,
  DialogoTitulo,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

const ETAPAS = [
  { chave: 'paciente', titulo: 'Dados do paciente' },
  { chave: 'solicitacao', titulo: 'Dados da solicitação' },
  { chave: 'revisao', titulo: 'Revisão e envio' },
] as const;

type Etapa = (typeof ETAPAS)[number]['chave'];

interface SolicitacaoCriada {
  id: string;
  protocolo: string;
}

interface PaginaNovaSolicitacaoProps {
  /**
   * Qual registro este formulário abre.
   *
   * A página é a mesma para as duas origens, parametrizada em lugar. São o
   * mesmo assistente de três etapas, os mesmos campos do paciente, a mesma
   * consulta ao PEC e a mesma revisão — o que muda é o esquema, o endpoint, um
   * bloco de dois campos e os textos.
   */
  origem?: OrigemSolicitacao;
}

export function PaginaNovaSolicitacao({ origem = 'UBS' }: PaginaNovaSolicitacaoProps = {}) {
  const navegar = useNavigate();
  const [etapa, setEtapa] = React.useState<Etapa>('paciente');
  /**
   * Solicitações já gravadas nesta sessão, na ordem em que saíram.
   *
   * Cada uma é gravada ao ser confirmada e já recebe protocolo: se a terceira
   * for barrada por duplicidade, as duas primeiras continuam válidas e o
   * trabalho de digitar o paciente não se perde.
   */
  const [criadas, setCriadas] = React.useState<SolicitacaoCriada[]>([]);
  /** Aberta logo após gravar: "outra para o mesmo paciente?". */
  const [perguntandoOutra, setPerguntandoOutra] = React.useState(false);
  const [concluido, setConcluido] = React.useState(false);
  /** Recusa por pedido repetido — fica na tela, e não num aviso que some. */
  const [duplicidade, setDuplicidade] = React.useState<string | null>(null);

  const reprimida = ehDemandaReprimida(origem);
  const { usuario } = useAuth();
  const perfil = usuario?.perfil;
  // Na demanda reprimida e no balcão do gestor, que não tem unidade própria.
  // O menu já vem restrito às unidades que o gestor acompanha.
  const escolheUbs = escolheUbsDeOrigem(origem, perfil);

  const { data: especialidades } = useQuery({
    queryKey: ['especialidades-ativas'],
    queryFn: () => api.get<Pick<Especialidade, 'id' | 'nome'>[]>('/especialidades/ativas'),
    staleTime: 5 * 60_000,
  });

  // Mesma chave da listagem e dos relatórios: normalmente já vem do cache.
  const { data: unidades } = useQuery({
    queryKey: ['ubs-ativas'],
    queryFn: () => api.get<UbsResumida[]>('/ubs/ativas'),
    staleTime: 5 * 60_000,
    enabled: escolheUbs,
  });

  const formulario = useForm<FormularioSolicitacao>({
    resolver: zodResolver(esquemaDaOrigem(origem, perfil)),
    mode: 'onBlur',
    defaultValues: {
      pacienteNome: '',
      pacienteDataNascimento: '',
      pacienteCpf: '',
      pacienteCns: '',
      pacienteTelefone: '',
      pacienteEmail: '',
      pacienteEndereco: '',
      pacienteNomeMae: '',
      tipo: 'CONSULTA',
      especialidadeId: '',
      procedimento: '',
      prioridade: 'ELETIVA',
      observacoes: '',
      ubsId: '',
      pedidoOriginalEm: '',
    },
  });

  const valores = formulario.watch();
  const idade = valores.pacienteDataNascimento
    ? calcularIdade(valores.pacienteDataNascimento)
    : null;

  /**
   * Preenche o formulário com o retorno do PEC.
   *
   * O telefone é o único campo que não chega de fora: por regra do KAVI, o
   * operador da unidade confirma o número com o paciente e digita. Um número
   * desatualizado vindo do cadastro daria a falsa impressão de estar conferido
   * — e é por ele que o paciente será avisado do agendamento.
   */
  const preencherComPec = (paciente: PacientePec) => {
    if (paciente.nome) formulario.setValue('pacienteNome', paciente.nome);
    if (paciente.dataNascimento) {
      formulario.setValue('pacienteDataNascimento', paciente.dataNascimento);
    }
    if (paciente.nomeMae) formulario.setValue('pacienteNomeMae', paciente.nomeMae);
    if (paciente.endereco) formulario.setValue('pacienteEndereco', paciente.endereco);
    if (paciente.email) formulario.setValue('pacienteEmail', paciente.email);
    if (paciente.cpf) formulario.setValue('pacienteCpf', mascararCpf(paciente.cpf));
    if (paciente.cns) formulario.setValue('pacienteCns', mascararCns(paciente.cns));

    void formulario.trigger(['pacienteNome', 'pacienteDataNascimento']);
  };

  const enviar = useMutation({
    mutationFn: (dados: FormularioSolicitacao) =>
      api.post<SolicitacaoCriada>(
        reprimida ? '/solicitacoes/demanda-reprimida' : '/solicitacoes',
        prepararEnvio(dados, origem, perfil),
      ),
    onSuccess: (resultado) => {
      setCriadas((anteriores) => [...anteriores, resultado]);
      setDuplicidade(null);
      // No balcão o mesmo paciente costuma sair com mais de um pedido; a
      // pergunta poupa redigitar os oito campos de identificação.
      if (reprimida) setConcluido(true);
      else setPerguntandoOutra(true);
    },
    onError: (erro) => {
      // 409 é o pedido repetido: a explicação diz qual protocolo já está na
      // fila, e o operador precisa dela à vista enquanto corrige o formulário.
      if (erro instanceof ErroApi && erro.status === 409) {
        setDuplicidade(erro.mensagemUsuario);
        return;
      }

      toast.error(
        erro instanceof ErroApi
          ? erro.mensagemUsuario
          : reprimida
            ? 'Não foi possível registrar a demanda reprimida.'
            : 'Não foi possível enviar a solicitação.',
      );
    },
  });

  const avancar = async () => {
    const campos = CAMPOS_POR_ETAPA[etapa as keyof typeof CAMPOS_POR_ETAPA];

    // Valida só a etapa atual: apontar erro de um campo que o usuário ainda
    // não viu seria confuso.
    if (campos) {
      // `trigger` espera um array mutável; a constante é readonly de propósito.
      const valido = await formulario.trigger([...campos] as FieldPath<FormularioSolicitacao>[]);
      if (!valido) return;
    }

    const indice = ETAPAS.findIndex((e) => e.chave === etapa);
    setEtapa(ETAPAS[Math.min(indice + 1, ETAPAS.length - 1)]!.chave);
  };

  const voltar = () => {
    const indice = ETAPAS.findIndex((e) => e.chave === etapa);
    setEtapa(ETAPAS[Math.max(indice - 1, 0)]!.chave);
  };

  /**
   * Outro pedido para o mesmo paciente.
   *
   * Limpa **só** os campos do pedido e volta à etapa 2. Os dados do paciente
   * ficam como estão e param de ser editáveis nesta sessão: as solicitações já
   * gravadas carregam o retrato que foi enviado, e deixar o cadastro mudar
   * daqui para a frente faria irmãs do mesmo atendimento discordarem sobre
   * quem é o paciente.
   */
  const outraParaOMesmoPaciente = () => {
    formulario.resetField('tipo', { defaultValue: 'CONSULTA' });
    formulario.resetField('especialidadeId', { defaultValue: '' });
    formulario.resetField('procedimento', { defaultValue: '' });
    formulario.resetField('prioridade', { defaultValue: 'ELETIVA' });
    formulario.resetField('observacoes', { defaultValue: '' });

    setPerguntandoOutra(false);
    setDuplicidade(null);
    setEtapa('solicitacao');
  };

  /** O paciente está travado a partir da segunda solicitação da sessão. */
  const pacienteReaproveitado = criadas.length > 0 && !concluido;

  // --- Confirmação -----------------------------------------------------------
  if (concluido && criadas.length > 0) {
    const varias = criadas.length > 1;

    return (
      <div className="mx-auto max-w-lg">
        <Cartao>
          <CartaoConteudo className="flex flex-col items-center gap-4 p-8 text-center">
            <CircleCheck className="text-sucesso size-14" aria-hidden="true" />
            <div>
              <h1 className="text-xl font-semibold">
                {reprimida
                  ? 'Demanda reprimida registrada'
                  : varias
                    ? `${criadas.length} solicitações registradas`
                    : 'Solicitação registrada'}
              </h1>
              <p className="text-silenciado-texto mt-1 text-sm">
                {reprimida
                  ? 'O pedido entrou na fila pela data informada. Anote o protocolo para acompanhamento.'
                  : varias
                    ? 'A Regulação já pode visualizá-las. Anote os protocolos para acompanhamento — cada pedido tem o seu.'
                    : 'A Regulação já pode visualizá-la. Anote o protocolo para acompanhamento.'}
              </p>
            </div>

            <ul className="flex w-full flex-col gap-2" role="status">
              {criadas.map((solicitacao) => (
                <li key={solicitacao.id} className="bg-silenciado rounded-lg border p-4">
                  <p className="text-silenciado-texto text-xs tracking-wide uppercase">Protocolo</p>
                  <p className="font-mono text-2xl font-semibold tracking-wider select-all">
                    {solicitacao.protocolo}
                  </p>
                </li>
              ))}
            </ul>

            <div className="flex w-full flex-col gap-2 sm:flex-row">
              {!varias && criadas[0] && (
                <Botao asChild className="flex-1">
                  <Link to={`/solicitacoes/${criadas[0].id}`}>
                    {reprimida ? 'Ver a demanda' : 'Ver a solicitação'}
                  </Link>
                </Botao>
              )}
              {varias && (
                <Botao asChild className="flex-1">
                  <Link to={caminhoDaLista(origem)}>Ver na listagem</Link>
                </Botao>
              )}
              <Botao
                variante="contorno"
                className="flex-1"
                onClick={() => {
                  formulario.reset();
                  setCriadas([]);
                  setConcluido(false);
                  setDuplicidade(null);
                  setEtapa('paciente');
                }}
              >
                Registrar para outro paciente
              </Botao>
            </div>
          </CartaoConteudo>
        </Cartao>
      </div>
    );
  }

  const indiceAtual = ETAPAS.findIndex((e) => e.chave === etapa);

  return (
    <div className="mx-auto max-w-2xl">
      <CabecalhoPagina
        titulo={reprimida ? 'Registrar demanda reprimida' : 'Nova solicitação'}
        descricao={
          reprimida
            ? 'Pedido anterior ao KAVI. O protocolo sai na série própria, começando por DR.'
            : 'O protocolo é gerado automaticamente ao concluir o envio.'
        }
      />

      {/* Indicador de etapas */}
      <ol className="mb-6 flex items-center gap-2" aria-label="Etapas do formulário">
        {ETAPAS.map((item, indice) => {
          const concluida = indice < indiceAtual || (pacienteReaproveitado && indice === 0);
          const atual = indice === indiceAtual;

          return (
            <li key={item.chave} className="flex flex-1 items-center gap-2">
              <span
                className={cn(
                  'flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                  concluida && 'bg-sucesso text-marca-contraste',
                  atual && 'bg-marca text-marca-contraste',
                  !concluida && !atual && 'bg-silenciado text-silenciado-texto',
                )}
                aria-current={atual ? 'step' : undefined}
              >
                {concluida ? <Check className="size-4" aria-hidden="true" /> : indice + 1}
              </span>
              <span
                className={cn(
                  'hidden text-sm sm:block',
                  atual ? 'font-medium' : 'text-silenciado-texto',
                )}
              >
                {item.titulo}
              </span>
              {indice < ETAPAS.length - 1 && (
                <span className="bg-borda ml-auto h-px flex-1" aria-hidden="true" />
              )}
            </li>
          );
        })}
      </ol>

      {/* No celular só os números cabem na trilha; sem esta linha, o usuário
          veria "2" sem saber do quê. */}
      <p className="text-silenciado-texto -mt-4 mb-5 text-sm sm:hidden">
        Etapa {indiceAtual + 1} de {ETAPAS.length}: {ETAPAS[indiceAtual]?.titulo}
      </p>

      <Cartao>
        <CartaoConteudo className="p-5">
          {/* Etapa 1 — Paciente */}
          {etapa === 'paciente' && (
            <div className="flex flex-col gap-4">
              {/* Documentos primeiro: são a chave de consulta ao PEC e evitam
                  redigitar o que o cadastro já tem. */}
              <div className="grid gap-4 sm:grid-cols-2">
                <Campo
                  id="pacienteCpf"
                  rotulo="CPF"
                  erro={formulario.formState.errors.pacienteCpf?.message}
                >
                  <Entrada
                    autoFocus
                    inputMode="numeric"
                    placeholder="000.000.000-00"
                    {...formulario.register('pacienteCpf', {
                      onChange: (evento: React.ChangeEvent<HTMLInputElement>) => {
                        formulario.setValue('pacienteCpf', mascararCpf(evento.target.value));
                      },
                    })}
                  />
                </Campo>

                <Campo
                  id="pacienteCns"
                  rotulo="Cartão Nacional de Saúde"
                  erro={formulario.formState.errors.pacienteCns?.message}
                >
                  <Entrada
                    inputMode="numeric"
                    placeholder="000 0000 0000 0000"
                    {...formulario.register('pacienteCns', {
                      onChange: (evento: React.ChangeEvent<HTMLInputElement>) => {
                        formulario.setValue('pacienteCns', mascararCns(evento.target.value));
                      },
                    })}
                  />
                </Campo>
              </div>

              <BuscaPec
                cpf={valores.pacienteCpf ?? ''}
                cns={valores.pacienteCns ?? ''}
                aoEncontrar={preencherComPec}
              />

              <hr className="border-borda" />

              <Campo
                id="pacienteNome"
                rotulo="Nome completo"
                obrigatorio
                ajuda="Gravado em caixa alta e sem acentuação, para conferir com o eSUS."
                erro={formulario.formState.errors.pacienteNome?.message}
              >
                <Entrada
                  autoComplete="off"
                  {...formulario.register('pacienteNome', {
                    onChange: (evento: React.ChangeEvent<HTMLInputElement>) => {
                      formulario.setValue('pacienteNome', paraAsciiMaiusculo(evento.target.value));
                    },
                  })}
                />
              </Campo>

              <div className="grid gap-4 sm:grid-cols-2">
                <Campo
                  id="pacienteDataNascimento"
                  rotulo="Data de nascimento"
                  obrigatorio
                  ajuda={idade !== null ? `${idade} anos` : undefined}
                  erro={formulario.formState.errors.pacienteDataNascimento?.message}
                >
                  <Entrada type="date" {...formulario.register('pacienteDataNascimento')} />
                </Campo>

                <Campo
                  id="pacienteNomeMae"
                  rotulo="Nome da mãe"
                  ajuda="Ajuda a Regulação a conferir a identidade."
                >
                  <Entrada
                    autoComplete="off"
                    {...formulario.register('pacienteNomeMae', {
                      onChange: (evento: React.ChangeEvent<HTMLInputElement>) => {
                        formulario.setValue(
                          'pacienteNomeMae',
                          paraAsciiMaiusculo(evento.target.value),
                        );
                      },
                    })}
                  />
                </Campo>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <Campo
                  id="pacienteTelefone"
                  rotulo="Telefone"
                  obrigatorio
                  ajuda="Confirme o número com o paciente. Não vem do PEC."
                  erro={formulario.formState.errors.pacienteTelefone?.message}
                >
                  <Entrada
                    inputMode="tel"
                    placeholder="(00) 90000-0000"
                    {...formulario.register('pacienteTelefone', {
                      onChange: (evento: React.ChangeEvent<HTMLInputElement>) => {
                        formulario.setValue(
                          'pacienteTelefone',
                          mascararTelefone(evento.target.value),
                        );
                      },
                    })}
                  />
                </Campo>

                <Campo
                  id="pacienteEmail"
                  rotulo="E-mail"
                  ajuda="Recebe o aviso de agendamento."
                  erro={formulario.formState.errors.pacienteEmail?.message}
                >
                  <Entrada type="email" {...formulario.register('pacienteEmail')} />
                </Campo>
              </div>

              <Campo
                id="pacienteEndereco"
                rotulo="Endereço"
                ajuda="Gravado em caixa alta e sem acentuação."
              >
                <Entrada
                  {...formulario.register('pacienteEndereco', {
                    onChange: (evento: React.ChangeEvent<HTMLInputElement>) => {
                      formulario.setValue(
                        'pacienteEndereco',
                        paraAsciiMaiusculo(evento.target.value),
                      );
                    },
                  })}
                />
              </Campo>

              <p className="text-silenciado-texto text-xs">
                CPF e CNS são opcionais enquanto a instituição não define a regra local de
                obrigatoriedade. Informe ao menos um sempre que possível — a Regulação depende deles
                para localizar o paciente no eSUS.
              </p>
            </div>
          )}

          {/* Etapa 2 — Solicitação */}
          {etapa === 'solicitacao' && (
            <div className="flex flex-col gap-4">
              {pacienteReaproveitado && (
                <div className="border-borda bg-silenciado flex items-start gap-2 rounded-md border p-3 text-sm">
                  <User
                    className="text-silenciado-texto mt-0.5 size-4 shrink-0"
                    aria-hidden="true"
                  />
                  <span>
                    Novo pedido para <strong>{valores.pacienteNome}</strong>
                    {valores.pacienteDataNascimento
                      ? `, nascido em ${formatarData(valores.pacienteDataNascimento)}`
                      : ''}
                    . Os dados do paciente vêm da solicitação anterior e não mudam aqui — cada
                    pedido registrado guarda o retrato que foi enviado.
                  </span>
                </div>
              )}

              {escolheUbs && (
                <>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Campo
                      id="ubsId"
                      rotulo="UBS de origem"
                      obrigatorio
                      ajuda={
                        reprimida
                          ? 'Unidade que encaminhou o pedido originalmente.'
                          : 'Unidade que encaminha o pedido, entre as que você acompanha.'
                      }
                      erro={formulario.formState.errors.ubsId?.message}
                    >
                      <Selecao {...formulario.register('ubsId')}>
                        <option value="">Selecione…</option>
                        {unidades?.map((unidade) => (
                          <option key={unidade.id} value={unidade.id}>
                            {unidade.nome}
                          </option>
                        ))}
                      </Selecao>
                    </Campo>

                    {reprimida && (
                      <Campo
                        id="pedidoOriginalEm"
                        rotulo="Data do pedido original"
                        ajuda="Opcional. Em branco, vale a data deste cadastro. Informada, é ela que define a posição na fila."
                        erro={formulario.formState.errors.pedidoOriginalEm?.message}
                      >
                        <Entrada
                          type="date"
                          max={hojeIso()}
                          {...formulario.register('pedidoOriginalEm')}
                        />
                      </Campo>
                    )}
                  </div>
                  <hr className="border-borda" />
                </>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <Campo
                  id="tipo"
                  rotulo="Tipo"
                  obrigatorio
                  erro={formulario.formState.errors.tipo?.message}
                >
                  <Selecao autoFocus={!escolheUbs} {...formulario.register('tipo')}>
                    {TIPOS_SOLICITACAO.map((tipo) => (
                      <option key={tipo} value={tipo}>
                        {ROTULOS_TIPO[tipo]}
                      </option>
                    ))}
                  </Selecao>
                </Campo>

                <Campo
                  id="especialidadeId"
                  rotulo="Especialidade"
                  obrigatorio
                  erro={formulario.formState.errors.especialidadeId?.message}
                >
                  <Selecao {...formulario.register('especialidadeId')}>
                    <option value="">Selecione…</option>
                    {especialidades?.map((especialidade) => (
                      <option key={especialidade.id} value={especialidade.id}>
                        {especialidade.nome}
                      </option>
                    ))}
                  </Selecao>
                </Campo>
              </div>

              <Campo
                id="procedimento"
                rotulo="Procedimento ou exame solicitado"
                ajuda="Descreva o que foi solicitado, quando aplicável."
              >
                <Entrada {...formulario.register('procedimento')} />
              </Campo>

              <Campo
                id="prioridade"
                rotulo="Prioridade"
                obrigatorio
                ajuda={DESCRICOES_PRIORIDADE[valores.prioridade ?? 'ELETIVA']}
              >
                <Selecao {...formulario.register('prioridade')}>
                  {PRIORIDADES.map((prioridade) => (
                    <option key={prioridade} value={prioridade}>
                      {ROTULOS_PRIORIDADE[prioridade]}
                    </option>
                  ))}
                </Selecao>
              </Campo>

              <Campo
                id="observacoes"
                rotulo="Observações"
                ajuda="Informações clínicas ou administrativas relevantes para a análise."
              >
                <AreaTexto rows={4} {...formulario.register('observacoes')} />
              </Campo>
            </div>
          )}

          {/* Etapa 3 — Revisão */}
          {etapa === 'revisao' && (
            <div className="flex flex-col gap-5">
              {duplicidade && (
                <div
                  className="border-status-devolvido-borda bg-status-devolvido-fundo text-status-devolvido-texto flex items-start gap-2 rounded-md border p-3 text-sm"
                  role="alert"
                >
                  <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                  <span>{duplicidade}</span>
                </div>
              )}

              <div className="border-status-enviado-borda bg-status-enviado-fundo text-status-enviado-texto flex items-start gap-2 rounded-md border p-3 text-sm">
                <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                <span>
                  {reprimida
                    ? 'Confira os dados antes de registrar. A demanda entra na fila da Regulação como qualquer outra, e a UBS de origem poderá acompanhá-la.'
                    : 'Confira os dados antes de enviar. Após o envio, apenas a Regulação altera a situação da solicitação — a UBS poderá acompanhar e anexar documentos.'}
                </span>
              </div>

              <Revisao titulo="Paciente">
                <Item rotulo="Nome" valor={valores.pacienteNome} />
                <Item
                  rotulo="Nascimento"
                  valor={
                    valores.pacienteDataNascimento
                      ? `${formatarData(valores.pacienteDataNascimento)}${idade !== null ? ` (${idade} anos)` : ''}`
                      : undefined
                  }
                />
                <Item rotulo="Nome da mãe" valor={valores.pacienteNomeMae} />
                <Item rotulo="CPF" valor={valores.pacienteCpf} />
                <Item rotulo="CNS" valor={valores.pacienteCns} />
                <Item rotulo="Telefone" valor={valores.pacienteTelefone} />
                <Item rotulo="E-mail" valor={valores.pacienteEmail} />
                <Item rotulo="Endereço" valor={valores.pacienteEndereco} />
              </Revisao>

              <Revisao titulo="Solicitação">
                {escolheUbs && (
                  <Item
                    rotulo="UBS de origem"
                    valor={unidades?.find((u) => u.id === valores.ubsId)?.nome}
                  />
                )}
                {reprimida && (
                  <Item
                    rotulo="Pedido original"
                    valor={
                      valores.pedidoOriginalEm
                        ? formatarData(valores.pedidoOriginalEm)
                        : 'Data deste cadastro'
                    }
                  />
                )}
                <Item rotulo="Tipo" valor={ROTULOS_TIPO[valores.tipo]} />
                <Item
                  rotulo="Especialidade"
                  valor={especialidades?.find((e) => e.id === valores.especialidadeId)?.nome}
                />
                <Item rotulo="Procedimento" valor={valores.procedimento} />
                <Item
                  rotulo="Prioridade"
                  valor={ROTULOS_PRIORIDADE[valores.prioridade ?? 'ELETIVA']}
                />
                <Item rotulo="Observações" valor={valores.observacoes} larguraTotal />
              </Revisao>
            </div>
          )}
        </CartaoConteudo>
      </Cartao>

      <div className="mt-4 flex items-center justify-between gap-2">
        {/* Com o paciente reaproveitado, a etapa 1 sai do caminho: os pedidos
            já gravados carregam o retrato que foi enviado, e deixar o cadastro
            mudar daqui faria irmãs do mesmo atendimento discordarem sobre quem
            é o paciente. */}
        <Botao
          variante="contorno"
          onClick={voltar}
          disabled={indiceAtual === 0 || (pacienteReaproveitado && etapa === 'solicitacao')}
        >
          <ArrowLeft aria-hidden="true" />
          Voltar
        </Botao>

        {etapa === 'revisao' ? (
          <Botao
            onClick={() => void formulario.handleSubmit((dados) => enviar.mutate(dados))()}
            disabled={enviar.isPending}
          >
            {enviar.isPending && <Loader2 className="animate-spin" aria-hidden="true" />}
            {enviar.isPending
              ? reprimida
                ? 'Registrando…'
                : 'Enviando…'
              : reprimida
                ? 'Registrar demanda'
                : 'Enviar solicitação'}
          </Botao>
        ) : (
          <Botao onClick={() => void avancar()}>
            Continuar
            <ArrowRight aria-hidden="true" />
          </Botao>
        )}
      </div>

      <div className="mt-4">
        <Botao
          variante="fantasma"
          tamanho="sm"
          onClick={() => void navegar(caminhoDaLista(origem))}
        >
          Cancelar e voltar à lista
        </Botao>
      </div>

      {/* Perguntada logo após gravar, e não antes: o pedido que acabou de sair
          já tem protocolo, e a resposta aqui só decide se a sessão continua. */}
      <Dialogo open={perguntandoOutra} onOpenChange={(aberto) => !aberto && setConcluido(true)}>
        <DialogoConteudo>
          <DialogoCabecalho>
            <DialogoTitulo>Deseja realizar outra solicitação para o mesmo paciente?</DialogoTitulo>
            <DialogoDescricao>
              O protocolo{' '}
              <strong className="font-mono">{criadas[criadas.length - 1]?.protocolo}</strong> já foi
              registrado. Continuando, os dados do paciente são reaproveitados e você volta direto
              para os dados da solicitação.
            </DialogoDescricao>
          </DialogoCabecalho>

          <DialogoRodape>
            <Botao
              variante="contorno"
              onClick={() => {
                setPerguntandoOutra(false);
                setConcluido(true);
              }}
            >
              Não, concluir
            </Botao>
            <Botao onClick={outraParaOMesmoPaciente}>
              <ArrowRight aria-hidden="true" />
              Sim, outra solicitação
            </Botao>
          </DialogoRodape>
        </DialogoConteudo>
      </Dialogo>
    </div>
  );
}

function Revisao({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="border-borda mb-2 border-b pb-1 text-sm font-semibold">{titulo}</h2>
      <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">{children}</dl>
    </section>
  );
}

function Item({
  rotulo,
  valor,
  larguraTotal,
}: {
  rotulo: string;
  valor?: string;
  larguraTotal?: boolean;
}) {
  return (
    <div className={cn(larguraTotal && 'sm:col-span-2')}>
      <dt className="text-silenciado-texto text-xs">{rotulo}</dt>
      <dd className={cn('text-sm', !valor && 'text-silenciado-texto')}>
        {valor?.trim() ? valor : 'Não informado'}
      </dd>
    </div>
  );
}
